export type FeedbackType = 'bug_report' | 'general_feedback' | 'feature_idea';
export type FeedbackMediaSlot = 'screenshot' | 'screen_recording';

export interface FeedbackMediaPolicy {
  acceptedMimeTypes: readonly string[];
  maxFileSizeBytes: number;
  maxFiles: number;
  maxDurationSeconds?: number;
}

export interface FeedbackMediaValidationResult {
  valid: boolean;
  message?: string;
}

export const FEEDBACK_TYPE_OPTIONS: readonly {
  value: FeedbackType;
  label: string;
  description: string;
}[] = [
  {
    value: 'bug_report',
    label: 'Bug report',
    description: 'Something is broken or behaving incorrectly.',
  },
  {
    value: 'general_feedback',
    label: 'General feedback',
    description: 'Share usability notes or general observations.',
  },
  {
    value: 'feature_idea',
    label: 'Feature idea',
    description: 'Suggest a capability that would improve the workflow.',
  },
];

export const FEEDBACK_MEDIA_POLICIES: Record<FeedbackMediaSlot, FeedbackMediaPolicy> = {
  screenshot: {
    acceptedMimeTypes: ['image/png', 'image/jpeg', 'image/webp'],
    maxFileSizeBytes: 10 * 1024 * 1024,
    maxFiles: 1,
  },
  screen_recording: {
    acceptedMimeTypes: ['video/mp4', 'video/webm', 'video/quicktime'],
    maxFileSizeBytes: 50 * 1024 * 1024,
    maxDurationSeconds: 120,
    maxFiles: 1,
  },
};

export const REQUIRED_FIELD_MESSAGES = {
  feedbackType: 'Choose a feedback type.',
  writtenFeedback: 'Enter written feedback.',
} as const;

export function validateRequiredFields(values: {
  feedbackType: FeedbackType | '';
  writtenFeedback: string;
}) {
  return {
    feedbackType: values.feedbackType ? undefined : REQUIRED_FIELD_MESSAGES.feedbackType,
    writtenFeedback: values.writtenFeedback.trim()
      ? undefined
      : REQUIRED_FIELD_MESSAGES.writtenFeedback,
  };
}

export function describeAcceptedTypes(policy: FeedbackMediaPolicy) {
  return policy.acceptedMimeTypes
    .map((mimeType) => mimeType.split('/')[1]?.toUpperCase() ?? mimeType)
    .join(', ');
}

export function formatBytes(bytes: number) {
  const megabytes = bytes / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)} MB`;
}

export function validateMediaFile(
  slot: FeedbackMediaSlot,
  file: File | undefined,
  durationSeconds?: number
): FeedbackMediaValidationResult {
  if (!file) {
    return { valid: true };
  }

  const policy = FEEDBACK_MEDIA_POLICIES[slot];

  if (!policy.acceptedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      message: `${slotLabel(slot)} must be ${describeAcceptedTypes(policy)}.`,
    };
  }

  if (file.size > policy.maxFileSizeBytes) {
    return {
      valid: false,
      message: `${slotLabel(slot)} must be ${formatBytes(policy.maxFileSizeBytes)} or smaller.`,
    };
  }

  if (
    slot === 'screen_recording' &&
    typeof durationSeconds === 'number' &&
    typeof policy.maxDurationSeconds === 'number' &&
    durationSeconds > policy.maxDurationSeconds
  ) {
    return {
      valid: false,
      message: `Screen recording must be ${policy.maxDurationSeconds} seconds or shorter.`,
    };
  }

  return { valid: true };
}

export function slotLabel(slot: FeedbackMediaSlot) {
  return slot === 'screenshot' ? 'Screenshot' : 'Screen recording';
}
